/**
 * Ops Portal — Reports Page (Phase 3: full CIT reporting).
 *
 * Layers of dynamic behaviour:
 *   1. tenant.profile_type — pharma vs bank catalogues.
 *   2. Theme-aware Tailwind (dark: prefix everywhere).
 *   3. Custom date range — preset chips + on-demand From/To pickers.
 *   4. Live-count "Details" cells — Van GPS Health & Route Alerts fetch
 *      their own summary endpoints so counts are always current.
 *   5. Expandable Van GPS Health row — click to reveal per-van roster.
 *   6. Compliance PDF list — recent CIT runs downloadable per-run.
 *
 * Auth is via HTTPOnly cookies; exports use `credentials: 'include'`.
 */
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  FileText, Calendar, TrendingUp, Thermometer,
  Package, Clock, Loader2, BarChart3, RefreshCw, AlertTriangle,
  FileSpreadsheet, FileType, MapPin, Route, Timer,
  ChevronDown, ChevronRight, ShieldCheck, Battery, Wifi, WifiOff,
} from 'lucide-react';
import LocationHistoryTimeline from './LocationHistoryTimeline';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
} from 'recharts';

const BANK_PROFILES = ['BANK', 'BANK_P2'];
const BANK_CODES = ['AXIS', 'HDFC'];

const REPORT_CATALOGUE = {
  pharma: [
    { key: 'temperature', label: 'Temperature Reports', icon: Thermometer, tone: 'text-blue-500 dark:text-blue-400' },
    { key: 'delivery',    label: 'Delivery Reports',    icon: Package,     tone: 'text-green-500 dark:text-green-400' },
    { key: 'device',      label: 'Device Reports',      icon: BarChart3,   tone: 'text-purple-500 dark:text-purple-400' },
    { key: 'alerts',      label: 'Alert Reports',       icon: AlertTriangle, tone: 'text-amber-500 dark:text-amber-400' },
  ],
  bank: [
    { key: 'km',       label: 'KM & Route Audit', icon: Route,         tone: 'text-teal-500 dark:text-teal-400' },
    { key: 'halts',    label: 'Halt Analysis',    icon: Timer,         tone: 'text-amber-500 dark:text-amber-400' },
    { key: 'devices',  label: 'Van GPS Health',   icon: MapPin,        tone: 'text-purple-500 dark:text-purple-400' },
    { key: 'alerts',   label: 'Route Alerts',     icon: AlertTriangle, tone: 'text-red-500 dark:text-red-400' },
  ],
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg">
      <p className="text-slate-900 dark:text-white font-medium mb-2">{label}</p>
      {payload.map((entry, idx) => (
        <p key={`t-${idx}`} style={{ color: entry.color }} className="text-sm">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

/**
 * Convert dateRange preset + explicit from/to into consistent params for the
 * various export endpoints. `custom` uses the from/to state; presets use hours.
 */
function useReportWindow(dateRange, fromDate, toDate) {
  return useMemo(() => {
    if (dateRange === 'custom') {
      const isReady = Boolean(fromDate && toDate);
      const days = isReady
        ? Math.max(1, Math.ceil((new Date(toDate) - new Date(fromDate)) / (24 * 3600 * 1000)) + 1)
        : 7;
      return { days, hours: days * 24, from: isReady ? fromDate : null, to: isReady ? toDate : null, isCustom: true, isReady };
    }
    const days = parseInt(dateRange || '7', 10);
    return { days, hours: days * 24, from: null, to: null, isCustom: false, isReady: true };
  }, [dateRange, fromDate, toDate]);
}

export default function OpsReportsPage() {
  const { tenant } = useTrackOticAuth();
  const isBankProfile = BANK_PROFILES.includes(tenant?.profile_type) || BANK_CODES.includes(tenant?.code);
  const catalogue = isBankProfile ? REPORT_CATALOGUE.bank : REPORT_CATALOGUE.pharma;

  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState(catalogue[0].key);
  const [dateRange, setDateRange] = useState('7');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const windowParams = useReportWindow(dateRange, fromDate, toDate);

  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({
    totalDeliveries: 0, avgDeliveryTime: '-', tempCompliance: '-', onTimeRate: '-',
  });

  // Bank-specific summaries (used both for KPIs and for the Details cell)
  const [vansHealth, setVansHealth] = useState(null);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [completedRuns, setCompletedRuns] = useState([]);
  const [bankKpiExtras, setBankKpiExtras] = useState(null);   // {halts_count, total_km}
  const [vansExpanded, setVansExpanded] = useState(false);

  const [temperatureHistory, setTemperatureHistory] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [deviceUptime, setDeviceUptime] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const valid = catalogue.some(r => r.key === reportType);
    if (!valid) setReportType(catalogue[0].key);
  }, [tenant?.id, catalogue, reportType]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const summaryRes = await opsApi.getReportsSummary(windowParams.days);
      setStats(summaryRes.data);

      let reportsRes;
      switch (reportType) {
        case 'temperature':
          reportsRes = await opsApi.getTemperatureReports(windowParams.days); break;
        case 'delivery':
          reportsRes = await opsApi.getDeliveryReports(windowParams.days); break;
        case 'device':
          reportsRes = await opsApi.getDeviceReports(); break;
        case 'alerts':
          reportsRes = await opsApi.getAlertReports(windowParams.days); break;
        case 'devices':
          reportsRes = { data: [{
            id: 'vans-health',
            type: 'devices',
            name: 'Live Van GPS Health',
            date: new Date().toISOString().slice(0, 10),
            status: 'ready',
          }] };
          break;
        case 'km':
        case 'halts':
          reportsRes = { data: [{
            id: `${reportType}-${new Date().toISOString().slice(0, 10)}`,
            type: reportType,
            name: reportType === 'km' ? 'Daily KM & Route Audit' : 'Halt Analysis (detailed)',
            date: new Date().toISOString().slice(0, 10),
            status: 'ready',
          }] };
          break;
        default:
          reportsRes = { data: [] };
      }
      setReports(reportsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [reportType, windowParams.days]);

  const fetchBankSummaries = useCallback(async () => {
    if (!isBankProfile) return;
    try {
      const qs = new URLSearchParams();
      if (windowParams.from) qs.set('from_date', windowParams.from);
      if (windowParams.to)   qs.set('to_date',   windowParams.to);

      const [vh, as, cr, dash] = await Promise.all([
        fetch(`${API_BASE}/api/v1/ops/reports/vans-health`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API_BASE}/api/v1/ops/reports/route-alerts-summary?${qs.toString()}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API_BASE}/api/v1/ops/reports/completed-runs?limit=20`, { credentials: 'include' }).then(r => r.json()),
        // Telemetry dashboard gives us the authoritative halts_count and total_distance_km
        fetch(`${API_BASE}/api/v1/ops/telemetry/dashboard?hours=${windowParams.hours}`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setVansHealth(vh);
      setAlertsSummary(as);
      setCompletedRuns(Array.isArray(cr) ? cr : []);
      if (dash && !dash.error) {
        setBankKpiExtras({
          halts_count: dash.kpis?.halts_count ?? 0,
          total_km: dash.kpis?.total_distance_km ?? 0,
        });
      }
    } catch (e) {
      // Non-fatal: KPI cards will fall back to placeholders
    }
  }, [API_BASE, isBankProfile, windowParams.from, windowParams.to, windowParams.hours]);

  const fetchChartData = useCallback(async () => {
    if (isBankProfile) { setChartLoading(false); return; }
    setChartLoading(true);
    try {
      const [tempRes, alertRes, deviceRes] = await Promise.all([
        opsApi.getTemperatureHistory(windowParams.days),
        opsApi.getAlertHistory(windowParams.days),
        opsApi.getDeviceUptime(windowParams.days),
      ]);
      setTemperatureHistory(tempRes.data);
      setAlertHistory(alertRes.data);
      setDeviceUptime(deviceRes.data);
    } catch (error) {
      /* silent */
    } finally {
      setChartLoading(false);
    }
  }, [isBankProfile, windowParams.days]);

  useEffect(() => {
    if (dateRange === 'custom' && (!fromDate || !toDate)) return;
    fetchReports();
    fetchChartData();
    fetchBankSummaries();
  }, [reportType, dateRange, fromDate, toDate, tenant?.id, fetchReports, fetchChartData, fetchBankSummaries]);

  const buildExportUrl = (format) => {
    // Bank tenants → new /ops/reports/ endpoints; Pharma tenants → classic
    // /reports/export/… scheme kept intact.
    const qs = new URLSearchParams();
    if (windowParams.isCustom) {
      qs.set('from_date', windowParams.from);
      qs.set('to_date', windowParams.to);
    } else {
      qs.set('hours', windowParams.hours);
    }
    if (isBankProfile) {
      if (reportType === 'halts') {
        return `${API_BASE}/api/v1/ops/reports/halts.xlsx?${qs.toString()}`;
      }
      // km / devices / alerts all pull from the same audit workbook
      const ext = format === 'pdf' ? 'xlsx' : format;
      return `${API_BASE}/api/v1/ops/reports/km.${ext}?${qs.toString()}`;
    }
    return `${API_BASE}/api/v1/reports/export/${format}/${reportType}?days=${windowParams.days}`;
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const url = buildExportUrl(format);
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Export failed');
      // Prefer the filename the server chose in Content-Disposition — the
      // backend uses a tenant + report + timestamp prefix (e.g.
      // "axis-halts-20260807-0629.xlsx") which the auditor recognises.
      const cd = response.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const ext = isBankProfile && format === 'pdf' ? 'xlsx' : format;
      const filename = match ? match[1] : `${reportType}_report.${ext}`;

      const blob = await response.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = dlUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`${ext.toUpperCase()} export downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const handleCompliancePdf = async (runId, wo_number) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/reports/compliance/${runId}.pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-${wo_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Compliance PDF for ${wo_number} downloaded`);
    } catch (e) {
      toast.error('Failed to generate compliance PDF');
    }
  };

  const currentReportMeta = useMemo(
    () => catalogue.find(r => r.key === reportType) || catalogue[0],
    [catalogue, reportType],
  );

  const getReportDetails = (report) => {
    switch (report.type) {
      case 'temperature':
        return `${report.records} readings, ${report.breaches} breaches, Avg: ${report.avgTemp}`;
      case 'delivery':
        return `${report.completed} completed, ${report.pending} pending, ${report.failed} failed`;
      case 'device':
        return `${report.online} online, ${report.offline} offline, ${report.battery_low ?? 0} low battery`;
      case 'alerts':
        if (isBankProfile && alertsSummary) {
          const t = alertsSummary.by_type || {};
          const s = alertsSummary.by_status || {};
          return (
            `${alertsSummary.total} total • ` +
            `${t.route_deviation || 0} deviation, ${t.device_offline || 0} offline, ${t.weight_drop || 0} weight-drop • ` +
            `${s.open || 0} open / ${s.acknowledged || 0} ack / ${s.resolved || 0} resolved`
          );
        }
        return `${report.total} total, ${report.critical} critical, ${report.resolved} resolved`;
      case 'devices':
        if (vansHealth) {
          const s = vansHealth.summary;
          return `${s.online} online / ${s.offline} offline • ${s.low_battery} low battery • avg ${s.avg_battery ?? '—'}% battery`;
        }
        return '—';
      case 'km':
        return 'Distance travelled, dwell time and route corridor variance per van';
      case 'halts':
        return 'Halts ≥ 5 min with duration, coords, address and geofence flag';
      default:
        return '-';
    }
  };

  const windowLabel = useMemo(() => {
    if (windowParams.isCustom && windowParams.isReady) return `${fromDate} → ${toDate}`;
    if (windowParams.isCustom) return 'Pick both dates';
    return `Last ${windowParams.days} days`;
  }, [windowParams.isCustom, windowParams.isReady, windowParams.days, fromDate, toDate]);

  const kpiCards = isBankProfile ? [
    { icon: Package,    tone: 'text-green-500 dark:text-green-400',  label: 'Runs Completed', value: stats.totalDeliveries ?? 0, sub: windowLabel },
    { icon: Route,      tone: 'text-teal-500 dark:text-teal-400',    label: 'Distance (km)',  value: bankKpiExtras ? `${bankKpiExtras.total_km.toFixed(1)}` : '…', sub: 'All vans, cumulative' },
    { icon: Timer,      tone: 'text-amber-500 dark:text-amber-400',  label: 'Halts (>5 min)', value: bankKpiExtras ? bankKpiExtras.halts_count : '…', sub: 'Unscheduled + branch stops' },
    { icon: TrendingUp, tone: 'text-purple-500 dark:text-purple-400', label: 'On-Time Rate',   value: stats.onTimeRate ?? '-',     sub: 'Corridor compliance' },
  ] : [
    { icon: Package,     tone: 'text-green-500 dark:text-green-400',  label: 'Total Deliveries',   value: stats.totalDeliveries,             sub: windowLabel },
    { icon: Clock,       tone: 'text-blue-500 dark:text-blue-400',    label: 'Avg Delivery Time',  value: stats.avgDeliveryTime,             sub: 'Across all routes' },
    { icon: Thermometer, tone: 'text-cyan-500 dark:text-cyan-400',    label: 'Temp Compliance',    value: stats.tempCompliance,              sub: `${stats.tempBreaches || 0} breaches` },
    { icon: TrendingUp,  tone: 'text-purple-500 dark:text-purple-400', label: 'On-Time Rate',       value: stats.onTimeRate,                  sub: 'Delivery performance' },
  ];

  return (
    <div className="p-8 min-h-screen bg-slate-50 dark:bg-transparent" data-testid="ops-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FileText className="w-8 h-8 text-teal-500" />
            Reports &amp; Analytics
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {isBankProfile
              ? 'GPS, route compliance and halt audits — export for regulatory filings'
              : 'Generate, visualize, and export operational reports'}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            variant="outline"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="reports-export-csv-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            variant="outline"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="reports-export-secondary-btn"
          >
            {isBankProfile ? <FileSpreadsheet className="w-4 h-4 mr-2" /> : <FileType className="w-4 h-4 mr-2" />}
            {isBankProfile ? 'Export XLSX' : 'Export PDF'}
          </Button>
          <Button
            onClick={() => { fetchReports(); fetchChartData(); fetchBankSummaries(); }}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="reports-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {kpiCards.map((c) => (
          <div
            key={c.label}
            className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6"
            data-testid={`reports-kpi-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <c.icon className={`w-5 h-5 ${c.tone}`} />
              <span className="text-slate-600 dark:text-slate-400 text-sm">{c.label}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{c.value}</p>
            <p className={`${c.tone} text-sm mt-1`}>{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6 flex-wrap items-center">
        <Select value={reportType} onValueChange={setReportType}>
          <SelectTrigger
            className="w-56 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-800 text-slate-900 dark:text-white"
            data-testid="report-type-select"
          >
            <SelectValue placeholder="Report Type" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            {catalogue.map(r => (
              <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger
            className="w-44 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-800 text-slate-900 dark:text-white"
            data-testid="date-range-select"
          >
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <SelectItem value="1">Last 24 Hours</SelectItem>
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
            <SelectItem value="90">Last 90 Days</SelectItem>
            <SelectItem value="custom">Custom range…</SelectItem>
          </SelectContent>
        </Select>

        {dateRange === 'custom' && (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Calendar className="w-4 h-4" />
              From
            </div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm rounded px-2 py-2"
              data-testid="reports-from-date"
            />
            <span className="text-slate-500">→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm rounded px-2 py-2"
              data-testid="reports-to-date"
            />
            {(!fromDate || !toDate) && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Pick both dates to run report</span>
            )}
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={isBankProfile ? 'table' : 'charts'} className="space-y-6">
        <TabsList className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
          {!isBankProfile && (
            <TabsTrigger value="charts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Charts
            </TabsTrigger>
          )}
          <TabsTrigger value="table" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
          {isBankProfile && (
            <TabsTrigger value="compliance" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Compliance PDF
            </TabsTrigger>
          )}
        </TabsList>

        {/* Charts (pharma) */}
        {!isBankProfile && (
          <TabsContent value="charts" className="space-y-6">
            {chartLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartPanel title="Temperature Trends" icon={Thermometer} iconTone="text-cyan-500 dark:text-cyan-400">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={temperatureHistory}>
                      <defs>
                        <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" />
                      <XAxis dataKey="label" stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <YAxis stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} domain={[-5, 15]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="avgTemp" name="Avg Temp (°C)" stroke="#0d9488" fill="url(#colorAvg)" strokeWidth={2} />
                      <Line type="monotone" dataKey="minTemp" name="Min Temp" stroke="#3b82f6" strokeWidth={1} dot={false} />
                      <Line type="monotone" dataKey="maxTemp" name="Max Temp" stroke="#ef4444" strokeWidth={1} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartPanel>

                <ChartPanel title="Alert Frequency" icon={AlertTriangle} iconTone="text-amber-500 dark:text-amber-400">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={alertHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" />
                      <XAxis dataKey="label" stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <YAxis stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="critical" name="Critical" fill="#ef4444" stackId="a" />
                      <Bar dataKey="warning" name="Warning" fill="#f59e0b" stackId="a" />
                      <Bar dataKey="info" name="Info" fill="#3b82f6" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartPanel>

                <ChartPanel title="Device Uptime" icon={BarChart3} iconTone="text-purple-500 dark:text-purple-400">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={deviceUptime}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" />
                      <XAxis dataKey="label" stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <YAxis stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} domain={[0, 100]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="uptimePercent" name="Uptime %" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartPanel>

                <ChartPanel title="Temperature Breaches" icon={Thermometer} iconTone="text-red-500 dark:text-red-400">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={temperatureHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-slate-700" />
                      <XAxis dataKey="label" stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <YAxis stroke="currentColor" className="text-slate-500 dark:text-slate-400" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="readings" name="Total Readings" fill="#0d9488" />
                      <Bar dataKey="breaches" name="Breaches" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartPanel>
              </div>
            )}
          </TabsContent>
        )}

        {/* Reports table */}
        <TabsContent value="table">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 dark:border-slate-800">
                  <TableHead className="text-slate-600 dark:text-slate-400">Report</TableHead>
                  <TableHead className="text-slate-600 dark:text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-600 dark:text-slate-400">Details</TableHead>
                  <TableHead className="text-slate-600 dark:text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-600 dark:text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      No reports available for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => {
                    const Icon = currentReportMeta.icon;
                    const isVanHealth = report.type === 'devices' && isBankProfile;
                    return (
                      <Fragment key={report.id}>
                        <TableRow className="border-slate-200 dark:border-slate-800">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {isVanHealth && (
                                <button
                                  onClick={() => setVansExpanded(x => !x)}
                                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
                                  data-testid="vans-health-expand-btn"
                                  aria-label="expand"
                                >
                                  {vansExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                              )}
                              <Icon className={`w-4 h-4 ${currentReportMeta.tone}`} />
                              <span className="text-slate-900 dark:text-white">{report.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700 dark:text-slate-300">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                              {report.date}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                            {getReportDetails(report)}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30">
                              {report.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {(reportType === 'km' || reportType === 'halts' || !isBankProfile) && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleExport('csv')}
                                    className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                                    data-testid={`download-csv-${report.id}`}>
                                    <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleExport('pdf')}
                                    className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                                    data-testid={`download-pdf-${report.id}`}>
                                    <FileType className="w-4 h-4 mr-1" />
                                    {isBankProfile ? 'XLSX' : 'PDF'}
                                  </Button>
                                </>
                              )}
                              {isVanHealth && (
                                <span className="text-xs text-slate-500">Expand to view per-van roster</span>
                              )}
                              {isBankProfile && reportType === 'alerts' && (
                                <span className="text-xs text-slate-500">Live counts (no export yet)</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isVanHealth && vansExpanded && vansHealth && (
                          <TableRow className="border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                            <TableCell colSpan={5} className="p-0">
                              <VanRoster vans={vansHealth.vans} apiBase={API_BASE} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Compliance PDF list */}
        {isBankProfile && (
          <TabsContent value="compliance">
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-teal-500" />
                  CIT Run Compliance Certificates
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Co-branded, tamper-evident PDF per completed run — includes route, distance, halts and a SHA-256 chain hash.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 dark:border-slate-800">
                    <TableHead className="text-slate-600 dark:text-slate-400">Run</TableHead>
                    <TableHead className="text-slate-600 dark:text-slate-400">Van</TableHead>
                    <TableHead className="text-slate-600 dark:text-slate-400">Route</TableHead>
                    <TableHead className="text-slate-600 dark:text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-600 dark:text-slate-400">Completed</TableHead>
                    <TableHead className="text-right text-slate-600 dark:text-slate-400">Certificate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedRuns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                        No completed CIT runs yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    completedRuns.map(run => (
                      <TableRow key={run.id} className="border-slate-200 dark:border-slate-800">
                        <TableCell className="font-mono text-slate-900 dark:text-white text-sm">
                          {run.work_order_number}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                          {run.van}
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                          <div className="max-w-md truncate">
                            {(run.pickup_address || '—')} → {(run.drop_address || '—')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            run.status === 'completed'
                              ? 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30'
                              : 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30'
                          }>
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {run.completed_at ? new Date(run.completed_at).toLocaleString() : (run.started_at ? `Started ${new Date(run.started_at).toLocaleString()}` : '—')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCompliancePdf(run.id, run.work_order_number)}
                            className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                            data-testid={`compliance-pdf-${run.work_order_number}`}
                          >
                            <FileType className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ChartPanel({ title, icon: Icon, iconTone, children }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconTone}`} />
        {title}
      </h3>
      {children}
    </div>
  );
}

function VanRoster({ vans, apiBase }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${apiBase}/api/v1/ops/reports/vans-health.xlsx`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', m ? m[1] : `van-health.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Van health snapshot downloaded');
    } catch (e) {
      toast.error('Failed to download van health snapshot');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Live snapshot • {vans.length} van{vans.length === 1 ? '' : 's'} tracked
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          disabled={downloading}
          className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          data-testid="van-health-xlsx-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          {downloading ? 'Preparing…' : 'Download XLSX'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {vans.map(v => {
          const isOnline = v.status === 'online';
          const batTone = v.battery_low
            ? 'text-red-600 dark:text-red-400'
            : (v.battery_level && v.battery_level > 60 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400');
          return (
            <div key={v.device_uid} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid={`van-card-${v.device_uid}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-slate-900 dark:text-white">{v.device_uid}</span>
                <Badge className={isOnline
                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                  : 'bg-slate-300 dark:bg-slate-700 text-slate-800 dark:text-slate-200'}>
                  {isOnline ? <><Wifi className="w-3 h-3 mr-1" /> ONLINE</> : <><WifiOff className="w-3 h-3 mr-1" /> OFFLINE</>}
                </Badge>
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs">
                <span className={`flex items-center gap-1 ${batTone}`}>
                  <Battery className="w-3 h-3" />
                  {v.battery_level ?? '—'}%
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {v.offline_minutes != null ? `Last seen ${v.offline_minutes} min ago` : 'Never'}
                </span>
              </div>
              {(v.current_lat != null && v.current_lng != null) && (
                <p className="mt-2 text-[10px] font-mono text-slate-500 dark:text-slate-500">
                  {v.current_lat.toFixed(5)}, {v.current_lng.toFixed(5)}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Movement history for all vans */}
      <div className="mt-6 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="van-location-history">
        <LocationHistoryTimeline hours={24} />
      </div>
    </div>
  );
}
